/**
 * Theme Provider - Merges preset themes with user overrides
 *
 * Presets are not imported here: each build entry (npm lib, legacy embed,
 * per-project variant embeds) registers the presets it ships via
 * registerThemePresets(). This keeps unused themes out of variant bundles.
 */
import { MergedThemeContext, ThemeConfig, ThemePreset } from '../components/Widget/ThemeContext';

const themeRegistry: Record<string, ThemePreset> = {};

/**
 * Register theme presets so they can be resolved via styleTheme.themeName.
 * Called by build entries; later registrations override earlier ones by name.
 */
export function registerThemePresets(presets: Record<string, ThemePreset>): void {
    Object.assign(themeRegistry, presets);
}

/**
 * Names of all currently registered theme presets
 */
export function getRegisteredThemeNames(): string[] {
    return Object.keys(themeRegistry);
}

/**
 * Deep merge two objects
 */
function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
    const output = { ...target };

    for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            output[key] = deepMerge(target[key] || {}, source[key] as any);
        } else if (source[key] !== undefined) {
            output[key] = source[key] as any;
        }
    }

    return output;
}

/**
 * Load and merge theme configuration
 * @param styleTheme - User theme configuration (must include themeName)
 * @returns Merged theme context with preset + overrides
 */
export function loadTheme(styleTheme?: ThemeConfig): MergedThemeContext {
    const availableThemes = getRegisteredThemeNames().join(', ') || '(none registered)';

    if (!styleTheme || !styleTheme.themeName) {
        throw new Error(
            `styleTheme.themeName is required. Available themes: ${availableThemes}. ` +
                'Example: styleTheme={{ themeName: "youna-orange" }}'
        );
    }

    const preset = themeRegistry[styleTheme.themeName];

    if (!preset) {
        throw new Error(
            `Theme preset "${styleTheme.themeName}" not found. Available themes: ${availableThemes}`
        );
    }

    // Merge preset with user overrides
    return {
        ...deepMerge(preset as any, styleTheme as any),
        preset,
    };
}

/**
 * Generate CSS variables from theme configuration
 * @param theme - Merged theme context
 * @returns CSS variable declarations as object
 */
export function generateCSSVariables(theme: MergedThemeContext): Record<string, string> {
    const vars: Record<string, string> = {};

    // Colors
    if (theme.colors) {
        if (theme.colors.primary) vars['--theme-color-primary'] = theme.colors.primary;
        if (theme.colors.background) vars['--theme-color-background'] = theme.colors.background;
        if (theme.colors.launcher) vars['--theme-color-launcher'] = theme.colors.launcher;
        if (theme.colors.userMessage) vars['--theme-color-user-message'] = theme.colors.userMessage;
        if (theme.colors.userMessageText)
            vars['--theme-color-user-message-text'] = theme.colors.userMessageText;
        if (theme.colors.assistantMessage)
            vars['--theme-color-assistant-message'] = theme.colors.assistantMessage;
        if (theme.colors.assistantMessageText)
            vars['--theme-color-assistant-message-text'] = theme.colors.assistantMessageText;
        if (theme.colors.headerBackground)
            vars['--theme-color-header-bg'] = theme.colors.headerBackground;
        if (theme.colors.headerText) vars['--theme-color-header-text'] = theme.colors.headerText;
        if (theme.colors.sendButton) vars['--theme-color-send-button'] = theme.colors.sendButton;
        if (theme.colors.sendButtonIcon)
            vars['--theme-color-send-button-icon'] = theme.colors.sendButtonIcon;
    }

    // Typography
    if (theme.typography) {
        if (theme.typography.fontFamily) vars['--theme-font-family'] = theme.typography.fontFamily;
        if (theme.typography.fontSize) {
            if (theme.typography.fontSize.base)
                vars['--theme-font-size-base'] = theme.typography.fontSize.base;
            if (theme.typography.fontSize.header)
                vars['--theme-font-size-header'] = theme.typography.fontSize.header;
            if (theme.typography.fontSize.message)
                vars['--theme-font-size-message'] = theme.typography.fontSize.message;
            if (theme.typography.fontSize.small)
                vars['--theme-font-size-small'] = theme.typography.fontSize.small;
        }
        if (theme.typography.fontWeight) {
            if (theme.typography.fontWeight.normal)
                vars['--theme-font-weight-normal'] = String(theme.typography.fontWeight.normal);
            if (theme.typography.fontWeight.medium)
                vars['--theme-font-weight-medium'] = String(theme.typography.fontWeight.medium);
            if (theme.typography.fontWeight.bold)
                vars['--theme-font-weight-bold'] = String(theme.typography.fontWeight.bold);
        }
        if (theme.typography.lineHeight) {
            if (theme.typography.lineHeight.tight)
                vars['--theme-line-height-tight'] = theme.typography.lineHeight.tight;
            if (theme.typography.lineHeight.normal)
                vars['--theme-line-height-normal'] = theme.typography.lineHeight.normal;
            if (theme.typography.lineHeight.relaxed)
                vars['--theme-line-height-relaxed'] = theme.typography.lineHeight.relaxed;
        }
    }

    // Spacing
    if (theme.spacing) {
        if (theme.spacing.message) vars['--theme-spacing-message'] = theme.spacing.message;
        if (theme.spacing.bubble) vars['--theme-spacing-bubble'] = theme.spacing.bubble;
        if (theme.spacing.container) vars['--theme-spacing-container'] = theme.spacing.container;
        if (theme.spacing.compact) vars['--theme-spacing-compact'] = theme.spacing.compact;
    }

    // Border radius
    if (theme.borderRadius) {
        if (theme.borderRadius.bubble)
            vars['--theme-border-radius-bubble'] = theme.borderRadius.bubble;
        if (theme.borderRadius.button)
            vars['--theme-border-radius-button'] = theme.borderRadius.button;
        if (theme.borderRadius.window)
            vars['--theme-border-radius-window'] = theme.borderRadius.window;
        if (theme.borderRadius.launcher)
            vars['--theme-border-radius-launcher'] = theme.borderRadius.launcher;
    }

    // Shadows
    if (theme.boxShadow) {
        if (theme.boxShadow.launcher) vars['--theme-shadow-launcher'] = theme.boxShadow.launcher;
        if (theme.boxShadow.window) vars['--theme-shadow-window'] = theme.boxShadow.window;
        if (theme.boxShadow.message) vars['--theme-shadow-message'] = theme.boxShadow.message;
    }

    // Dimensions
    if (theme.dimensions) {
        if (theme.dimensions.height) vars['--theme-window-height'] = theme.dimensions.height;
        if (theme.dimensions.width) vars['--theme-window-width'] = theme.dimensions.width;
        if (theme.dimensions.maxWidth) vars['--theme-window-max-width'] = theme.dimensions.maxWidth;
        if (theme.dimensions.minWidth) vars['--theme-window-min-width'] = theme.dimensions.minWidth;
        if (theme.dimensions.padding) vars['--theme-window-padding'] = theme.dimensions.padding;
    }

    // Animations
    if (theme.animations) {
        if (theme.animations.speed) vars['--theme-animation-speed'] = theme.animations.speed;
        if (theme.animations.easing) vars['--theme-animation-easing'] = theme.animations.easing;
    }

    // Custom CSS variables from user
    if (theme.cssVariables) {
        Object.assign(vars, theme.cssVariables);
    }

    return vars;
}

/**
 * List of all possible theme CSS variables (for cleanup)
 */
const ALL_THEME_VARIABLES = [
    '--theme-color-primary',
    '--theme-color-background',
    '--theme-color-launcher',
    '--theme-color-user-message',
    '--theme-color-user-message-text',
    '--theme-color-assistant-message',
    '--theme-color-assistant-message-text',
    '--theme-color-header-bg',
    '--theme-color-header-text',
    '--theme-color-send-button',
    '--theme-color-send-button-icon',
    '--theme-font-family',
    '--theme-font-size-base',
    '--theme-font-size-header',
    '--theme-font-size-message',
    '--theme-font-size-small',
    '--theme-font-weight-normal',
    '--theme-font-weight-medium',
    '--theme-font-weight-bold',
    '--theme-line-height-tight',
    '--theme-line-height-normal',
    '--theme-line-height-relaxed',
    '--theme-spacing-message',
    '--theme-spacing-bubble',
    '--theme-spacing-container',
    '--theme-spacing-compact',
    '--theme-border-radius-bubble',
    '--theme-border-radius-button',
    '--theme-border-radius-window',
    '--theme-border-radius-launcher',
    '--theme-shadow-launcher',
    '--theme-shadow-window',
    '--theme-shadow-message',
    '--theme-window-height',
    '--theme-window-width',
    '--theme-window-max-width',
    '--theme-window-min-width',
    '--theme-window-padding',
    '--theme-animation-speed',
    '--theme-animation-easing',
    // Custom CSS variables
    '--theme-user-message-border-radius',
    '--theme-user-message-border',
    '--theme-assistant-message-border-radius',
    '--theme-assistant-message-border',
    '--theme-send-button-border-radius',
    '--theme-send-button-border',
    '--theme-send-button-border-left',
    '--theme-send-button-padding',
    '--theme-background-gradient',
    '--theme-input-border',
    '--theme-input-border-radius',
    '--theme-input-bg',
    '--theme-input-padding',
    '--theme-sender-border-top',
    '--theme-sender-padding',
    '--theme-sender-align-items',
    '--theme-input-height',
    '--theme-button-suggestion-bg',
    '--theme-button-suggestion-border',
    '--theme-button-suggestion-border-radius',
    '--theme-button-suggestion-hover-bg',
    '--theme-message-max-width',
    '--theme-spacing-bubble-horizontal',
    '--theme-font-family-bold',
    '--theme-disclaimer-color',
    '--theme-disclaimer-font-size',
    '--theme-sender-min-height',
    '--theme-widget-conversation-margin',
    '--theme-widget-conversation-gap',
    '--theme-window-max-height',
    '--theme-disclaimer-padding',
    '--theme-launcher-display-when-open',
    '--theme-overlay-backdrop',
];

/**
 * Apply CSS variables to an element
 * @param element - DOM element to apply variables to
 * @param theme - Merged theme context
 */
export function applyCSSVariables(element: HTMLElement, theme: MergedThemeContext): void {
    // Clear all existing theme variables first
    for (const varName of ALL_THEME_VARIABLES) {
        element.style.removeProperty(varName);
    }

    // Apply new theme variables
    const vars = generateCSSVariables(theme);

    for (const [key, value] of Object.entries(vars)) {
        element.style.setProperty(key, value);
    }
}
